// Catálogo de razones de rechazo y sus mensajes para el usuario final
const REJECTION_REASON = {
  DOC_LOW_SCORE: 'doc_low_score',
  DOC_EXPIRED: 'doc_expired',
  DOC_ALTERED: 'doc_altered',
  DOC_AI_GENERATED: 'doc_ai_generated',
  DATA_MISMATCH: 'data_mismatch',
  POA_TOO_OLD: 'poa_too_old',
  MAX_RETRIES: 'max_retries',
  LIVENESS_FAILED: 'liveness_failed',
  // Error técnico en el pipeline de extracción (Gemini caído, timeout, etc.)
  // Permite reintento — el documento en sí no es el problema.
  SERVICE_ERROR: 'service_error',
  FACE_MISMATCH: 'face_mismatch',
  // Error técnico en comparación facial (sin cara detectada, Rekognition caído, etc.)
  // Retryable — el sujeto debe repetir la prueba de vida.
  FACE_COMPARISON_FAILED: 'face_comparison_failed',
  SESSION_EXPIRED: 'session_expired',
} as const;

type RejectionReason = (typeof REJECTION_REASON)[keyof typeof REJECTION_REASON];

// Estructura de mensaje de rechazo con instrucción de reintento si aplica
interface RejectionMessage {
  title: string;
  description: string;
  canRetry: boolean;
  retryInstruction?: string;
}

// Mensajes en español orientados al usuario final del casino
const REJECTION_MESSAGES: Record<RejectionReason, RejectionMessage> = {
  doc_low_score: {
    title: 'No pudimos verificar tu documento',
    description:
      'La imagen de tu identificación no cumple con los requisitos mínimos de calidad o autenticidad.',
    canRetry: true,
    retryInstruction:
      'Asegúrate de que tu documento esté bien iluminado, sin reflejos y completamente visible dentro del encuadre.',
  },
  doc_expired: {
    title: 'Identificación no vigente',
    description: 'La fecha de vigencia de tu documento ha expirado y no puede ser aceptada.',
    canRetry: true,
    retryInstruction:
      'Presenta una identificación oficial vigente: INE actualizada o Pasaporte con fecha de expiración futura.',
  },
  doc_altered: {
    title: 'Documento no válido',
    description:
      'Se detectaron indicios de que el documento ha sido modificado o alterado. Por seguridad, no podemos continuar.',
    canRetry: false,
  },
  doc_ai_generated: {
    title: 'Documento no válido',
    description:
      'El documento parece haber sido generado digitalmente y no corresponde a una identificación oficial física.',
    canRetry: false,
  },
  data_mismatch: {
    title: 'Los datos no coinciden',
    description:
      'La información de tu documento no coincide con los datos registrados en tu cuenta de Amarello.',
    canRetry: true,
    retryInstruction:
      'Verifica que estés usando el documento de identidad que corresponds a la cuenta con la que iniciaste sesión.',
  },
  poa_too_old: {
    title: 'Comprobante de domicilio vencido',
    description: 'El comprobante de domicilio debe tener una fecha de emisión de los últimos 3 meses.',
    canRetry: true,
    retryInstruction:
      'Sube un recibo de luz, agua, gas, teléfono o estado de cuenta bancario con fecha reciente.',
  },
  max_retries: {
    title: 'Número máximo de intentos alcanzado',
    description:
      'Has superado el número máximo de intentos permitidos para este documento. Tu solicitud no puede continuar.',
    canRetry: false,
  },
  liveness_failed: {
    title: 'Verificación facial no exitosa',
    description:
      'No fue posible confirmar que la persona frente a la cámara está presente físicamente.',
    canRetry: true,
    retryInstruction:
      'Asegúrate de estar en un lugar bien iluminado, sin lentes de sol ni cubrebocas, y sigue las instrucciones en pantalla.',
  },
  face_mismatch: {
    title: 'La persona no coincide con el documento',
    description:
      'La persona frente a la cámara no coincide con la fotografía del documento de identidad presentado.',
    canRetry: false,
  },
  face_comparison_failed: {
    title: 'No pudimos verificar tu identidad',
    description:
      'Ocurrió un error técnico al comparar tu rostro con el documento de identidad. Por favor, repite la prueba de vida.',
    canRetry: true,
    retryInstruction:
      'Asegúrate de estar en un lugar bien iluminado, sin objetos que cubran tu rostro, y sigue las instrucciones en pantalla.',
  },
  session_expired: {
    title: 'Sesión expirada',
    description: 'Tu sesión de verificación ha expirado. Por favor, inicia el proceso nuevamente desde Amarello.com.',
    canRetry: false,
  },
  service_error: {
    title: 'Error en el procesamiento',
    description:
      'Ocurrió un error técnico al procesar tu documento. El sistema no pudo completar la verificación en este momento.',
    canRetry: true,
    retryInstruction:
      'Intenta de nuevo. Si el problema persiste, espera unos minutos y vuelve a intentarlo.',
  },
};

export { REJECTION_REASON, REJECTION_MESSAGES };
export type { RejectionReason, RejectionMessage };
